import { FloaterBar, PostTemplate, MainProduct } from 'types/globalTypes';
import { useState, useEffect } from 'react';
import { useACF } from '@context/ACFContext';
import { useIsMobile } from '@hooks/useIsMobile';
import glideImage from '@utils/glideImage';
import { Button } from '@components/core/button/Button';
import { FloaterStyled } from './floater.styled.ts';
import Star from '@assets/icons/Star';

const Floater: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const {
    floaterBar,
    generalSettings,
    postTemplate,
  }: {
    floaterBar?: FloaterBar;
    generalSettings?: any;
    postTemplate?: PostTemplate;
  } = useACF();

  // Get data
  const mainProduct = postTemplate?.review_page_v4_products_section_settings?.main_product as MainProduct;
  if (!mainProduct || !floaterBar || floaterBar.floater_show === false) {
    return null;
  }

  const { trust_pilot_rating_score, tr_trust_pilot_excellent_label } = mainProduct.trustpilot_component;
  const { tr_button_text, button_cta_link} = mainProduct.button_component;

  const isMobile = useIsMobile(744); // mobile breakpoint
  const floaterImage = floaterBar.product_image?.url || mainProduct.product_image;


  // Define floater appearance
  useEffect(() => {
    
    if (!isMobile) { // hide on desktop
      setIsVisible(false);
      return;
    }
    
    // always show on mobile    
    if (floaterBar.floater_logic === 'always') {
      setIsVisible(true);
      return;
    }

    // otherwise show after scroll
    const handleScroll = () => setIsVisible(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);

  }, [isMobile, floaterBar]);

  // Get data from Main Product or Floater bar overrides
  return (
    <FloaterStyled id="floating-cta" isVisible={isVisible}>
      <div className="floating-cta__header">
        <div className="floating-cta__header__wrapper">
          <div className="floating-cta__header__number">#1</div>
          <div className="floating-cta__header__text">
            {floaterBar.tr_description && (
              <>
                {floaterBar.tr_description}
                <div className="floating-cta__header__text__divider"></div>
              </>
            )}
            <a
              href={button_cta_link.url}
              target={button_cta_link.target ? '_blank' : '_self'}
            >
            {mainProduct.product_name}
            </a>
          </div>
        </div>
      </div>

      <div className="floating-cta__body">  
        <div className="floating-cta__content">
          <img
              className="floating-cta__contentImage"
              src={floaterImage && glideImage(floaterImage, { w: 48, q: 80 })}
              alt={generalSettings?.brand_name}
              loading="lazy"
              decoding="async"
              width={48}
              height="auto"
            />
            <div className="floating-cta__contentDivider"></div>
            <div className="floating-cta__contentText">
              <div className="floating-cta__contentTextRating">
                {trust_pilot_rating_score}
                <Star rating={floaterBar.stars || 5} size={8} gap={2} />
              </div>
              <div>{tr_trust_pilot_excellent_label}</div>
            </div>

          <Button
            className="floating-cta__button"
            href={button_cta_link.url}
            target={button_cta_link.target ? '_blank' : '_self'}
            primary={true}
          >
            {floaterBar.tr_cta_text || tr_button_text}
          </Button>
        </div>
      </div>
    </FloaterStyled>
  );
};

export default Floater;